import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApplicationQueryKey } from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
import {
  cancelExecutionsBulk,
  type BulkCancelResponse,
} from './workflowExecutions.api';
import type { UnderwayActivity } from './types';

// Fires BE `bulk_cancel` for the activity's pending executions —
// triggered by the user-confirmed Stop action. BE archives the
// executions (status: cancelled, skip_reason: "cancelled_by_user").
// Cache invalidation refreshes the drawer/candidate list so the
// stage-actions row reflects the cancellation.
export const useUnderwayCancel = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      activity: UnderwayActivity
    ): Promise<BulkCancelResponse | undefined> => {
      if (activity.executionIds.length === 0) return undefined;
      const result = await cancelExecutionsBulk(activity.executionIds);

      if (activity.jobId) {
        queryClient.invalidateQueries({
          queryKey: ['getCandidateList', Number(activity.jobId)],
        });
      }
      activity.candidates.forEach((c) => {
        queryClient.invalidateQueries({
          queryKey: getApplicationQueryKey(String(c.id)),
        });
      });
      // Caller surfaces a warning when `skipped_ids` is non-empty —
      // those executions already left `pending` and will still fire.
      return result;
    },
    [queryClient]
  );
};
